/**
 * Payloads the Executive Summary needs on top of the dashboard metrics:
 * investment allocation and issue lead time.
 *
 * Executive Summary is the primary-ICP page (VP/Director of Engineering), and
 * the question it answers is "where is the engineering budget actually going".
 * So the allocation is shaped to show a real finding rather than an even split:
 * maintenance and tech debt together outweigh feature work, which is the
 * conversation this page exists to start.
 */

import type { InvestmentAllocationResponse, WorkType, WorkTypeAllocation } from '@/types/insights';
import type { IssueMetricsResponse } from '@/types/forecasting';
import { DEMO_ORG_ID, resolveProfile } from '../fixture';
import { demoDate, jitter, rngFor, type DemoSeedInput } from '../seed';

export interface DemoExecutiveParams {
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
}

const seedInput = (params: DemoExecutiveParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.repoId,
  teamId: params.teamId,
  excludeBots: params.excludeBots,
});

/**
 * Baseline split, and the colours the chart already uses per work type.
 *
 * Weights deliberately do NOT sum to a tidy 100: they are normalised below, so
 * tuning one does not silently break the others.
 */
const WORK_TYPES: Array<{ type: WorkType; label: string; color: string; weight: number }> = [
  { type: 'feature', label: 'New features', color: '#3B82F6', weight: 34 },
  { type: 'maintenance', label: 'Maintenance', color: '#F59E0B', weight: 22 },
  { type: 'tech_debt', label: 'Tech debt', color: '#A855F7', weight: 18 },
  { type: 'bug_fix', label: 'Bug fixes', color: '#EF4444', weight: 14 },
  { type: 'infrastructure', label: 'Infrastructure', color: '#06B6D4', weight: 8 },
  { type: 'support', label: 'Support', color: '#64748B', weight: 4 },
];

export function buildDemoInvestmentAllocation(
  params: DemoExecutiveParams
): InvestmentAllocationResponse {
  const profile = resolveProfile(params.repoId, params.teamId);
  const rng = rngFor(seedInput(params, 'executive/investment'), 'allocations');

  // A churny repo spends proportionally more on debt and fixes, less on new
  // features - so selecting payments-service shifts the pie the way its own
  // metrics say it should, rather than showing the org average again.
  const debtPull = (profile.churnMultiplier - 1) * 18;

  const weights = WORK_TYPES.map(({ type, weight }) => {
    const shifted =
      type === 'feature'
        ? weight - debtPull
        : type === 'tech_debt' || type === 'bug_fix'
          ? weight + debtPull / 2
          : weight;
    return Math.max(1, shifted * jitter(rng, 0.08));
  });

  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const totalPrs = Math.round(profile.prsPerDay * params.days * 0.92);
  const totalLoc = Math.round(totalPrs * profile.avgPrSize);

  const allocations: WorkTypeAllocation[] = WORK_TYPES.map((wt, i) => {
    const share = weights[i] / totalWeight;
    return {
      work_type: wt.type,
      label: wt.label,
      pr_count: Math.round(totalPrs * share),
      pr_percentage: Number((share * 100).toFixed(1)),
      // LOC per PR varies by work type: infrastructure changes are large,
      // support fixes are small. A flat LOC share would make the two charts
      // identical and the second one pointless.
      loc: Math.round(totalLoc * share * (wt.type === 'infrastructure' ? 1.4 : wt.type === 'support' ? 0.5 : 1)),
      loc_percentage: Number((share * 100).toFixed(1)),
      color: wt.color,
    };
  });

  const uncategorized = Math.round(totalPrs * 0.06);

  return {
    organization_id: DEMO_ORG_ID,
    time_period: `${params.days}d`,
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    allocations,
    total_prs: totalPrs,
    total_loc: totalLoc,
    uncategorized_count: uncategorized,
    uncategorized_percentage: Number(((uncategorized / Math.max(totalPrs, 1)) * 100).toFixed(1)),
    // Not 100%: a real org never classifies everything, and a demo claiming
    // perfect coverage is the kind of detail that reads as fake.
    classification_coverage: 94,
  };
}

export function buildDemoIssueMetrics(params: DemoExecutiveParams): IssueMetricsResponse {
  const profile = resolveProfile(params.repoId, params.teamId);
  const rng = rngFor(seedInput(params, 'executive/issues'), 'lead-time');
  const totalPrs = Math.round(profile.prsPerDay * params.days * 0.92);

  // Percentiles must be ordered avg <= p90 and median <= p90, or the chart
  // renders a p90 bar shorter than its own median.
  const percentiles = (baseHours: number, subKey: string) => {
    const r = rngFor(seedInput(params, 'executive/issues'), subKey);
    const median = baseHours * jitter(r, 0.1);
    return {
      avg: Number((median * 1.18).toFixed(1)),
      median: Number(median.toFixed(1)),
      p90: Number((median * 2.6).toFixed(1)),
      count: Math.round(totalPrs * 0.71),
    };
  };

  return {
    organization_id: DEMO_ORG_ID,
    time_period: `${params.days}d`,
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    // Dwell time is the wait before anyone starts - typically far longer than
    // the delivery itself, which is the point this metric makes.
    backlog_dwell_time: percentiles(profile.cycleTimeHours * 2.4, 'dwell'),
    issue_to_merge_time: percentiles(profile.cycleTimeHours * 3.6, 'merge'),
    issue_to_first_commit_time: percentiles(profile.cycleTimeHours * 2.7, 'commit'),
    issues_with_prs: Math.round(totalPrs * 0.71),
    prs_without_issues: Math.round(totalPrs * 0.29 * jitter(rng, 0.1)),
    total_merged_prs: totalPrs,
    has_issue_tracker: true,
  };
}
